import { z } from 'zod';
import { ClientStatusSchema, PaymentMethodSchema, SepaSchemeSchema } from './client';
import { ExpenseCategorySchema, ImputationTypeSchema } from './invoiceReceived';
import { InvoiceStatusSchema } from './invoice';

// CSV inputs come as strings from papaparse. We coerce numbers and trim strings.
const Str = z.string().transform((v) => v.trim());
const Num = z
  .string()
  .transform((v) => v.replace(/\s/g, '').replace(/,/g, '.'))
  .pipe(z.coerce.number());
const NumOpt = z.preprocess((v) => (v === '' || v == null ? undefined : v), Num).optional();

// Accepts ISO 'YYYY-MM-DD' or Spanish '17 may 2026' or '17/05/2026'.
const MONTHS_ES: Record<string, string> = {
  ene: '01', feb: '02', mar: '03', abr: '04', may: '05', jun: '06',
  jul: '07', ago: '08', sep: '09', oct: '10', nov: '11', dic: '12',
};

export function parseSpanishDate(input: string): string | null {
  const s = input.trim().toLowerCase();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const mDmy = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})$/);
  if (mDmy) {
    const [, d, m, y] = mDmy;
    return `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}`;
  }
  const mEs = s.match(/^(\d{1,2})\s+([a-záéí]{3,4})\s+(\d{4})$/);
  if (mEs) {
    const [, d, mon, y] = mEs;
    const mm = MONTHS_ES[mon!.slice(0, 3)];
    if (!mm) return null;
    return `${y}-${mm}-${d!.padStart(2, '0')}`;
  }
  return null;
}

const DateField = z
  .string()
  .transform((v, ctx) => {
    const r = parseSpanishDate(v);
    if (!r) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Invalid date: ${v}` });
      return z.NEVER;
    }
    return r;
  });

// Empty cells from papaparse arrive as '' — treat them as absent so optional
// fields (dates, enums) don't fail validation on blank columns.
const emptyToUndef = (v: unknown) => (v === '' || v == null ? undefined : v);
const DateOpt = z.preprocess(emptyToUndef, DateField).optional();

export const ClientCsvRowSchema = z.object({
  name: Str,
  nif: Str,
  ownerCode: Str.optional(),
  status: ClientStatusSchema.optional().default('activo'),
  email: Str.optional(),
  phone: Str.optional(),
  address: Str.optional(),
  city: Str.optional(),
  postalCode: Str.optional(),
  country: Str.optional(),
  paymentMethod: z.preprocess(emptyToUndef, PaymentMethodSchema).optional(),
  iban: Str.optional(),
  bic: Str.optional(),
  bankName: Str.optional(),
  accountHolder: Str.optional(),
  sepaMandateRef: Str.optional(),
  sepaMandateDate: DateOpt,
  sepaScheme: z.preprocess(emptyToUndef, SepaSchemeSchema).optional(),
  paymentTermsDays: NumOpt,
  creditLimit: NumOpt,
});
export type ClientCsvRow = z.infer<typeof ClientCsvRowSchema>;

export const InvoiceIssuedCsvRowSchema = z.object({
  formattedNumber: Str.optional(),
  clientNif: Str,
  ownerCode: Str.optional(),
  status: InvoiceStatusSchema.optional().default('borrador'),
  concept: Str.optional().default(''),
  issueDate: DateField.optional(),
  sendDate: DateField.optional(),
  payDate: DateField.optional(),
  dueDate: DateField.optional(),
  baseAmount: Num,
  irpfPct: NumOpt,
  ivaPct: NumOpt,
});
export type InvoiceIssuedCsvRow = z.infer<typeof InvoiceIssuedCsvRowSchema>;

export const InvoiceReceivedCsvRowSchema = z.object({
  supplier: Str,
  reference: Str.optional(),
  date: DateField,
  category: ExpenseCategorySchema,
  baseAmount: Num,
  ivaAmount: NumOpt,
  totalAmount: Num,
  imputationType: ImputationTypeSchema,
  clientNif: Str.optional(),
});
export type InvoiceReceivedCsvRow = z.infer<typeof InvoiceReceivedCsvRowSchema>;

export const ImportMode = z.enum(['merge', 'skip', 'fail']);
export type ImportMode = z.infer<typeof ImportMode>;
